<script setup lang="ts">
import type { BookingAccess, BookingCredentials, ErrorDto } from '@optionway/models';
import type { ComponentPublicInstance } from 'vue';

import { modelsImpl } from '@optionway/models';
import { TicketsPlane, X } from 'lucide-vue-next';
import { z } from 'zod';

import { formatBookingLastName, formatBookingReference } from './format-field';

const { $appConfig } = useNuxtApp();
const localePath = useLocalePath();
const bookingAccessStore = useBookingAccessStore();

const prefix = 'components.myBooking.login';

const loginSchema = z.object({
  supplierReference: z
    .string({ required_error: `${prefix}.errors.bookingReferenceRequired` })
    .trim()
    .min(1, `${prefix}.errors.bookingReferenceRequired`)
    .min(6, `${prefix}.errors.bookingReferenceLength`)
    .max(40, `${prefix}.errors.bookingReferenceLength`)
    .regex(/^[A-Z0-9/]+$/, `${prefix}.errors.bookingReferenceCharacters`),
  lastName: z
    .string({ required_error: `${prefix}.errors.lastNameRequired` })
    .trim()
    .min(1, `${prefix}.errors.lastNameRequired`)
    .max(40, `${prefix}.errors.lastNameLength`),
});

const bookingReferenceId = useId();
const lastNameId = useId();
const bookingReferenceInput = useTemplateRef<ComponentPublicInstance>('bookingReferenceInput');

const form = ref(new modelsImpl.BookingCredentialsImpl());
const submitted = ref(false);
const claimError = ref<'invalidCredentials' | 'generic'>();

const { errors, isValid } = useZodForm(loginSchema, () => form.value, submitted);

const { mutate: claim, asyncStatus } = useMutation<BookingAccess, BookingCredentials, ErrorDto>({
  key: ['my-booking', 'claim'],
  // spread: $fetch only JSON-encodes plain objects, a BookingCredentialsImpl instance would be sent as "[object Object]"
  mutation: (credentials) =>
    $fetch<BookingAccess>(`${$appConfig.api?.url}/my-booking/claim`, { method: 'POST', body: { ...credentials } }),
  onSuccess: async (bookingAccess) => {
    bookingAccessStore.bookingAccess = bookingAccess;
    await navigateTo(localePath('my-booking'));
  },
  onError: (error) => {
    claimError.value = error.statusCode === 401 ? 'invalidCredentials' : 'generic';
  },
});

const closeClaimError = () => {
  claimError.value = undefined;
  unrefElement(bookingReferenceInput)?.focus();
};

const onSubmit = () => {
  submitted.value = true;
  claimError.value = undefined;
  if (isValid.value) {
    claim(form.value);
  }
};
</script>

<template>
  <div class="grid overflow-hidden rounded-2xl border border-gray-300 bg-white md:grid-cols-2 xl:grid-cols-5">
    <UiCustomResponsivePicture
      src="my-booking/login-hero.webp"
      sizes="100vw md:50vw xl:683px"
      alt=""
      class="h-32 md:order-last md:h-full xl:col-span-3"
      :img-attrs="{ class: 'size-full object-cover object-bottom md:object-center' }"
    />
    <form
      class="flex flex-col gap-6 p-4 lg:p-8 xl:col-span-2"
      @submit.prevent="onSubmit"
    >
      <div class="flex flex-col gap-6">
        <div class="flex items-center gap-3 lg:flex-col lg:items-start lg:gap-6">
          <div class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-primary-50 lg:size-20">
            <TicketsPlane
              class="size-6 text-brand-primary-700 lg:size-10"
              :stroke-width="1.5"
              aria-hidden="true"
            />
          </div>
          <h1 class="text-xl font-bold text-text-primary-900 lg:text-3xl">{{ $t(`${prefix}.title`) }}</h1>
        </div>
        <p class="pb-2 text-sm font-normal text-text-secondary-600">{{ $t(`${prefix}.subtitle`) }}</p>
      </div>
      <UiField>
        <div class="flex flex-col gap-1 lg:flex-row lg:items-center">
          <UiFieldLabel :for="bookingReferenceId">{{ $t(`${prefix}.bookingReferenceLabel`) }}</UiFieldLabel>
          <NuxtLink
            class="text-sm font-normal text-brand-primary-700 underline"
            :to="$t(`${prefix}.bookingReferenceHelpUrl`)"
            target="_blank"
            :title="`${$t(`${prefix}.bookingReferenceHelp`)} (${$t('components.common.newWindow')})`"
          >
            {{ $t(`${prefix}.bookingReferenceHelp`) }}
          </NuxtLink>
        </div>
        <UiInput
          :id="bookingReferenceId"
          ref="bookingReferenceInput"
          v-model="form.supplierReference"
          :format="formatBookingReference"
          type="text"
          maxlength="40"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          class="font-normal text-text-primary-900"
          :class="{ 'border-red-500': errors.supplierReference }"
          :aria-invalid="!!errors.supplierReference || undefined"
          :aria-describedby="
            errors.supplierReference ? `${bookingReferenceId}-error` : `${bookingReferenceId}-description`
          "
        />
        <UiFieldError
          v-if="errors.supplierReference"
          :id="`${bookingReferenceId}-error`"
        >
          {{ $t(errors.supplierReference) }}
        </UiFieldError>
        <UiFieldDescription
          v-else
          :id="`${bookingReferenceId}-description`"
        >
          {{ $t(`${prefix}.bookingReferenceHint`) }}
        </UiFieldDescription>
      </UiField>
      <UiField>
        <UiFieldLabel :for="lastNameId">{{ $t(`${prefix}.lastNameLabel`) }}</UiFieldLabel>
        <UiInput
          :id="lastNameId"
          v-model="form.lastName"
          :format="formatBookingLastName"
          type="text"
          maxlength="40"
          autocomplete="family-name"
          autocapitalize="characters"
          spellcheck="false"
          class="font-normal text-text-primary-900"
          :class="{ 'border-red-500': errors.lastName }"
          :aria-invalid="!!errors.lastName || undefined"
          :aria-describedby="errors.lastName ? `${lastNameId}-error` : undefined"
        />
        <UiFieldError
          v-if="errors.lastName"
          :id="`${lastNameId}-error`"
        >
          {{ $t(errors.lastName) }}
        </UiFieldError>
      </UiField>
      <UiAlert
        v-if="claimError"
        variant="error"
        size="m"
      >
        <div class="flex flex-col gap-1">
          <UiAlertTitle>{{ $t(`${prefix}.errors.${claimError}`) }}</UiAlertTitle>
          <UiAlertDescription>
            {{ $t(`${prefix}.errors.invalidCredentialsDescription`) }}
          </UiAlertDescription>
        </div>
        <button
          type="button"
          class="ml-auto flex h-full"
          @click="closeClaimError"
        >
          <X
            aria-hidden="true"
            class="h-4 w-4"
          />
          <span class="sr-only">{{ $t('components.common.close') }}</span>
        </button>
      </UiAlert>
      <UiButton
        type="submit"
        size="lg"
        shape="lg"
        :loading="asyncStatus === 'loading'"
        :loading-label="$t('components.common.loading')"
      >
        {{ $t(`${prefix}.submit`) }}
      </UiButton>
    </form>
  </div>
</template>
